import Cookies from 'js-cookie';
import { Controller } from 'stimulus';
import events from 'lib/events';
import { triggerCustomEvent, isHomepage, splitTestEnabled } from 'lib/helpers';
import SearchStateManager from 'lib/search-state';

const searchState = new SearchStateManager();
searchState.addPopStateListener(); // only add this once

export default class SearchFormController extends Controller {
  static targets = [
    'form',
    'input',
    'submit',
    'autocompleteSuggestions',
    'inlineFilters',
    'currentLabel',
    'previousSearches',
    'previousSearchTerms',
  ];

  navigationKeyCodes = [
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight',
    'ArrowDown',
    'Enter',
  ];

  exclusionKeyCodes = [
    'MetaLeft',
    'MetaRight',
    'ControlLeft',
    'ControlRight',
    'AltLeft',
    'AltRight',
    'ShiftLeft',
    'ShiftRight',
  ];

  wrapperToggleClasses = [
    'ez-search-form__inline-filters',
    'ez-search-form__inline-filters__arrow',
    'ez-search-form__inline-filters__current',
  ];

  connect() {
    this.autocompleteUrl = this.inputTarget.dataset.autocompleteSource;

    // If not mobile/auto-focus search input & add autocomplete listener
    this.checkMobile();

    // --------------------------------------
    if (!this.isMobile() && window.EezyScrollPositionHelper.allowFocus()) {
      this.setSearchFormFocus();
      window.EezyScrollPositionHelper.resetScrollPosition();
    }

    if (splitTestEnabled('vertical-grid-infinite-scroll')) {
      const filterEl = document.querySelector('.search-sidebar__sticky-wrapper__inner');
      if (filterEl) window.EezyStickyScrollPositionHelper.resetScrollPosition(filterEl);
    }

    if (this.hasInlineFiltersTarget) this.setupInlineFilters();

    this.checkAndToggleIsTyped();

    // Add is-ready class to search field, so we can apply styles after focused
    // (if we ever remove auto-focus, we need to change/re-think this)
    this.inputTarget.classList.add('is-ready');


    if (this.resultsCount === '0') {
      triggerCustomEvent(events.search.without_results, { searchTerm: this.searchTerm });
    }

    if (this.pageNumber === '1' && this.searchType) {
      triggerCustomEvent(events.search.search_type,
        {
          searchType: this.searchType,
          searchTerm: this.searchTerm,
          searchContentType: this.searchContentType,
        });
    }

    if (isHomepage()) this.clearSearchFiltersHidden();

    const previousSearchData = window.localStorage.getItem('previous_searches');
    if (previousSearchData) {
      this.setPreviousSearches(previousSearchData);
    } else {
      this.previousSearchesTarget.classList.add('is-hidden');
    }

    this.inputTarget.addEventListener('click', this.handleInputClick);
  }

  disconnect() {
    document.removeEventListener('click', this.blurInlineFilters);
    this.inputTarget.removeEventListener('click', this.handleInputClick);
  }

  setupInlineFilters() {
    this.setLandingPageInlineFilterDefaults();

    document.addEventListener('click', this.blurInlineFilters);
  }

  setSearchFormFocus() {
    const searchVal = this.inputTarget.value;
    this.inputTarget.value = '';
    this.inputTarget.focus();
    this.inputTarget.value = searchVal;
  }

  handleInputClick = () => {
    if (this.inputTarget.value.length >= 2) {
      this.queryAutocomplete(this.inputTarget.value);
    }
  }

  clearSearchFiltersHidden() {
    Cookies.remove('ezSearchFiltersHidden');
  }

  checkMobile() {
    if (!this.isMobile()) {
      const searchVal = this.inputTarget.value;
      this.inputTarget.value = '';
      this.inputTarget.focus();
      this.inputTarget.value = searchVal;
    }
  }

  setLandingPageInlineFilterDefaults() {
    if (document.body.classList.contains('resource-landing')) {
      searchState.resetStateToDefault();
    } else {
      this.setTogglesFromState();
    }
  }

  setTogglesFromState = () => {
    const activeTogglesFromState = searchState.getRawParamKeys();

    const allFiltersInputs = [
      ...this.element.querySelectorAll('.ez-input--checkbox input'),
    ];

    allFiltersInputs.forEach((checkbox) => {
      const checkboxFilterParam = checkbox.closest('.ez-input--checkbox').getAttribute('data-filter-param');

      if (activeTogglesFromState.includes(checkboxFilterParam)) {
        this.setActiveFilter(checkbox);
      }
    });
  }

  clearPreviousSearches() {
    this.previousSearchesTarget.classList.add('is-hidden');
    window.localStorage.removeItem('previous_searches');
  }

  setPreviousSearches(previousSearchData) {
    const previousSearches = JSON.parse(previousSearchData);

    for (let i = 0; i < previousSearches.length; i += 1) {
      const previousSearch = JSON.parse(previousSearches[i]);
      const previousSearchHtml = `
        <li>
          <a class="ez-btn--light"
             data-action="click->search-form#handlePreviousSearchClick"
             data-content-type="${previousSearch.contentType}">${previousSearch.term}</a>
        </li>`;
      this.previousSearchTermsTarget.insertAdjacentHTML('beforeend', previousSearchHtml);
    }
  }

  handlePreviousSearchClick(event) {
    this.inputTarget.value = event.target.textContent;
    // Dont currently change content type from previous search
    // this.updateContentType(event.target.getAttribute('data-content-type'));
    this.formTarget.classList.remove('is-suggesting');
    this.submit(event);
  }

  handlePreviousClickBlur(event) {
    event.preventDefault();
  }

  handleFilterClick = (event) => {
    this.wrapperToggleClasses.forEach((wrapperClass) => {
      if (event.target.classList.contains(wrapperClass)) this.toggleInlineFilters();
    });
  }

  setActiveFilter = (inputEl) => {
    const allFilters = [...this.element.querySelectorAll('input')];
    const optionItem = inputEl.closest('.ez-search-form__inline-filters__item');

    allFilters.forEach((el) => { el.checked = false; });

    inputEl.checked = true;

    this.currentLabelTarget.textContent = optionItem.dataset.selected;
    this.inputTarget.placeholder = optionItem.dataset.searchSelected;

    triggerCustomEvent(events.search.update_content_type, {
      contentType: inputEl.value,
    });
  }

  handleFilterOptionClick = (event) => {
    this.setActiveFilter(event.target);

    if (!searchState.omittedFromState(event.target.getAttribute('name'))) {
      searchState.updateParamKey(`${event.target.getAttribute('name')}-${event.target.getAttribute('value')}`);
    }

    this.toggleInlineFilters();
    this.trackFilterOptionChange(event.target.getAttribute('value'));

    if (document.body.classList.contains('searches-show')) {
      if (searchState.omittedFromState(event.target.getAttribute('name'))) {
        triggerCustomEvent(events.search.apply_filters, {
          contentType: event.target.value,
        });
      } else {
        this.submit(event);
      }

      this.toggleInlineFilters();
    } else {
      this.updateContentType(event.target.value);
      this.setSearchFormFocus();
    }

    if (window.innerWidth < 1000) {
      this.toggleInlineFilters();
    }
  }

  updateContentType(newType) {
    this.inputTarget.setAttribute('data-content-type', newType);

    triggerCustomEvent(events.search.update_content_type, {
      contentType: newType,
    });
  }

  trackFilterOptionChange = (filterValue) => {
    const trackParams = {
      filterValue,
      searchTerm: this.searchInputContents,
    };

    triggerCustomEvent(events.search.header_search_filter, trackParams);
  }

  toggleInlineFilters = () => {
    this.inlineFiltersTarget.classList.toggle('is-open');
  }

  blurInlineFilters = (event) => {
    if (this.inlineFiltersTarget.classList.contains('is-open')
    && !event.target.closest('.ez-search-form__inline-filters')) {
      this.toggleInlineFilters();
    }
  }

  checkAndToggleIsTyped() {
    if (this.searchInputContents) {
      this.element.classList.add('is-typed');
    } else {
      this.element.classList.remove('is-typed');
    }
  }

  handleInputKeyUp = (event) => {
    if (!this.exclusionKeyCodes.includes(event.code) && !this.navigationKeyCodes.includes(event.code)) {
      this.checkAndToggleIsTyped();
    }

    if (this.navigationKeyCodes.includes(event.code) && document.querySelector('.ez-search-form__suggestions li')) {
      this.navigateSuggestions(event.code);
    } else if (!this.exclusionKeyCodes.includes(event.code) && this.inputTarget.value.length >= 2) {
      this.queryAutocomplete(this.inputTarget.value);
    } else if (this.autocompleteSuggestionsTarget.innerHTML !== '') {
      this.resetAutocomplete();
    }
  }

  handleInputKeyDown = (event) => {
    if (event.metaKey && event.code === 'Enter') {
      this.submit(event);
    }
  }

  handleSuggestionClick = (event) => {
    this.inputTarget.value = event.target.textContent;
    this.submit(event);
  }

  navigateSuggestions = (keyCode) => {
    const suggestionsList = document.querySelector('.ez-search-form__suggestions');
    const activeSuggestion = suggestionsList.querySelector('li.is-active');

    const navUp = () => {
      if (activeSuggestion && activeSuggestion.previousSibling) {
        activeSuggestion.classList.remove('is-active');
        activeSuggestion.previousSibling.classList.add('is-active');
      } else if (activeSuggestion && !activeSuggestion.previousSibling) {
        activeSuggestion.classList.remove('is-active');
        suggestionsList.querySelector('li:last-of-type').classList.add('is-active');
      } else {
        suggestionsList.querySelector('li:last-of-type').classList.add('is-active');
      }
      this.inputTarget.value = suggestionsList.querySelector('li.is-active').textContent;
    };

    const navDown = () => {
      if (activeSuggestion && activeSuggestion.nextSibling) {
        activeSuggestion.classList.remove('is-active');
        activeSuggestion.nextSibling.classList.add('is-active');
      } else if (activeSuggestion && !activeSuggestion.nextSibling) {
        activeSuggestion.classList.remove('is-active');
        suggestionsList.querySelector('li:first-of-type').classList.add('is-active');
      } else {
        suggestionsList.querySelector('li:first-of-type').classList.add('is-active');
      }
      this.inputTarget.value = suggestionsList.querySelector('li.is-active').textContent;
    };

    if (keyCode === 'ArrowUp') {
      navUp();
    } else if (keyCode === 'ArrowDown') {
      navDown();
    } else if (keyCode === 'Enter') {
      this.resetAutocomplete();
    }
  }

  resetAutocomplete = () => {
    this.data.set('lastTerm', '');
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.remove('is-suggesting');
  }

  renderAutocomplete(res, req) {
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.add('is-suggesting');

    if (!res.length > 0) {
      this.resetAutocomplete();
      return;
    }

    Object.keys(res).forEach((i) => {
      const node = document.createElement('LI');
      const formattedReq = req.toLowerCase();

      if (res[i].indexOf(formattedReq) >= 0) {
        node.innerHTML = res[i].replace(formattedReq, `<strong>${formattedReq}</strong>`);
      } else {
        node.innerHTML = res[i];
      }

      node.dataset.action = 'mousedown->search-form#handleSuggestionClick';
      this.autocompleteSuggestionsTarget.appendChild(node);
    });
  }

  queryAutocomplete = (req) => {
    if (req === this.data.get('lastTerm')) return;
    this.data.set('lastTerm', this.inputTarget.value);

    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${this.autocompleteUrl}?term=${encodeURI(req)}`, true);
    xhr.setRequestHeader('Content-Type', 'text/plain');

    xhr.onload = () => {
      this.renderAutocomplete(JSON.parse(xhr.response), req);
    };

    xhr.send();
  }

  isMobile() {
    return document.body.classList.contains('is-mobile');
  }

  resetPagination() {
    searchState.updateStateValue('page', 1);
  }

  submitViaButton(event) {
    event.preventDefault();
    this.submit(event);
  }

  submit(event) {
    this.resetPagination();

    const searchedEventDetail = {
      term: this.inputTarget.value,
      searchRoute: this.inputTarget.getAttribute('data-search-route'),
      useSearchState: true,
      contentType: this.inputTarget.getAttribute('data-content-type'),
    };

    if (event.metaKey && event.code && event.code === 'Enter') {
      searchedEventDetail.searchInNewTab = true;
    }

    Cookies.set('ezSearchType', 'internal');
    triggerCustomEvent('Searched', searchedEventDetail);

    this.savePreviousSearch(searchedEventDetail);
  }

  savePreviousSearch(searchedEventDetail) {
    let previousSearches = JSON.parse(window.localStorage.getItem('previous_searches'));
    if (!previousSearches) {
      previousSearches = [];
    }
    for (let i = 0; i < previousSearches.length; i += 1) {
      const previousSearch = JSON.parse(previousSearches[i]);
      if (previousSearch.term === searchedEventDetail.term) {
        previousSearches.splice(i, 1);
        break;
      }
    }
    if (previousSearches.unshift(JSON.stringify(searchedEventDetail)) > 6) {
      previousSearches.pop();
    }
    window.localStorage.setItem('previous_searches', JSON.stringify(previousSearches));
  }

  get resultsCount() {
    const searchInfo = document.querySelector('[data-search-results-count]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-results-count');
    }

    return null;
  }

  get pageNumber() {
    const queryString = window.location.search.slice(1);
    const params = new URLSearchParams(queryString);

    if (params.has('page')) {
      return params.get('page');
    }

    return '1';
  }

  get searchTerm() {
    return this.element.getAttribute('data-search-term').replace(/ /g, '-');
  }

  get searchType() {
    return this.element.getAttribute('data-search-type');
  }

  get searchContentType() {
    return this.inputTarget.getAttribute('data-content-type');
  }

  get searchInputContents() {
    return this.inputTarget.value;
  }
}
